import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, parse, isValid } from 'date-fns';
import { it } from 'date-fns/locale';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import type { Event, EventType } from '../../api/types';

function formatDisplayDate(isoDate: string): string {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T00:00:00');
  if (!isValid(d)) return isoDate;
  return format(d, 'dd/MM/yyyy', { locale: it });
}

function parseDisplayDate(text: string): string {
  if (!text) return '';
  const d = parse(text, 'dd/MM/yyyy', new Date());
  if (!isValid(d)) return text;
  return format(d, 'yyyy-MM-dd');
}

interface EditEventModalProps {
  open: boolean;
  onClose: () => void;
  event: Event;
  onSave: (data: Record<string, unknown>) => Promise<void>;
  isSaving: boolean;
}

const EVENT_TYPES: { value: EventType; label: string }[] = [
  { value: 'MATRIMONIO', label: 'Matrimonio' },
  { value: 'BATTESIMO', label: 'Battesimo' },
  { value: 'COMUNIONE', label: 'Comunione' },
  { value: 'CRESIMA', label: 'Cresima' },
  { value: 'COMPLEANNO', label: 'Compleanno' },
  { value: 'RITRATTO', label: 'Ritratto' },
  { value: 'ALTRO', label: 'Altro' },
];

export default function EditEventModal({ open, onClose, event, onSave, isSaving }: EditEventModalProps) {
  const [form, setForm] = useState<Record<string, unknown>>({});

  useEffect(() => {
    if (open) {
      setForm({
        eventType: event.eventType,
        eventDate: event.eventDate ? event.eventDate.substring(0, 10) : '',
        startTime: event.startTime ?? '',
        endTime: event.endTime ?? '',
        location: event.location ?? '',
        totalAmount: event.totalAmount ?? '',
        notes: event.notes ?? '',
        syncToGoogle: event.syncToGoogle ?? false,
      });
    }
  }, [open, event]);

  const set = (key: string, value: unknown) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    const data: Record<string, unknown> = {};

    if (form.eventType !== event.eventType) data.eventType = form.eventType;
    if (form.eventDate && form.eventDate !== (event.eventDate ? event.eventDate.substring(0, 10) : '')) {
      data.eventDate = new Date(form.eventDate as string).toISOString();
    }
    if (form.startTime !== (event.startTime ?? '')) {
      data.startTime = (form.startTime as string).trim() || null;
    }
    if (form.endTime !== (event.endTime ?? '')) {
      data.endTime = (form.endTime as string).trim() || null;
    }
    if (form.location !== (event.location ?? '')) {
      data.location = (form.location as string).trim() || null;
    }
    if (String(form.totalAmount) !== String(event.totalAmount ?? '')) {
      const val = parseFloat(form.totalAmount as string);
      if (!isNaN(val) && val >= 0) data.totalAmount = val;
      else { toast.error('Importo totale non valido'); return; }
    }
    if (form.notes !== (event.notes ?? '')) {
      data.notes = (form.notes as string).trim() || null;
    }
    if (form.syncToGoogle !== event.syncToGoogle) data.syncToGoogle = form.syncToGoogle;

    if (Object.keys(data).length === 0) {
      onClose();
      return;
    }

    await onSave(data);
    onClose();
  };

  const contractSigned = event.contractSignedAt != null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Modifica evento"
      width={520}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Annulla
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving} icon={isSaving ? <Loader2 size={14} className="spin" /> : undefined}>
            Salva
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* Event Type */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Tipo evento</span>
          <select
            value={form.eventType as string}
            onChange={e => set('eventType', e.target.value)}
            style={{
              background: 'var(--glass-bg-card)',
              color: 'var(--color-text)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              padding: '0.5rem 0.75rem',
              fontSize: 'var(--text-sm)',
              width: '100%',
            }}
          >
            {EVENT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>

        {/* Date + Times row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Data</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="GG/MM/AAAA"
              value={formatDisplayDate(form.eventDate as string)}
              onChange={e => set('eventDate', parseDisplayDate(e.target.value))}
              style={{
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                padding: '0.5rem 0.75rem',
                fontSize: 'var(--text-sm)',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Ora inizio</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              pattern="[0-2][0-9]:[0-5][0-9]"
              value={form.startTime as string}
              onChange={e => set('startTime', e.target.value)}
              style={{
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                padding: '0.5rem 0.75rem',
                fontSize: 'var(--text-sm)',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Ora fine</span>
            <input
              type="text"
              inputMode="numeric"
              placeholder="HH:MM"
              pattern="[0-2][0-9]:[0-5][0-9]"
              value={form.endTime as string}
              onChange={e => set('endTime', e.target.value)}
              style={{
                background: 'var(--glass-bg-card)',
                color: 'var(--color-text)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-card)',
                padding: '0.5rem 0.75rem',
                fontSize: 'var(--text-sm)',
              }}
            />
          </label>
        </div>

        {/* Location */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Location</span>
          <input
            type="text"
            value={form.location as string}
            onChange={e => set('location', e.target.value)}
            placeholder="Luogo dell'evento"
            style={{
              background: 'var(--glass-bg-card)',
              color: 'var(--color-text)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              padding: '0.5rem 0.75rem',
              fontSize: 'var(--text-sm)',
            }}
          />
        </label>

        {/* Total amount */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>
            Totale concordato {contractSigned && '(bloccato)'}
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={form.totalAmount as string}
            onChange={e => set('totalAmount', e.target.value)}
            disabled={contractSigned}
            style={{
              background: contractSigned ? 'var(--glass-bg-card)' : 'var(--glass-bg-card)',
              color: 'var(--color-text)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              padding: '0.5rem 0.75rem',
              fontSize: 'var(--text-sm)',
              opacity: contractSigned ? 0.6 : 1,
            }}
          />
        </label>

        {/* Notes */}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Note</span>
          <textarea
            value={form.notes as string}
            onChange={e => set('notes', e.target.value)}
            placeholder="Note sull'evento"
            rows={3}
            style={{
              background: 'var(--glass-bg-card)',
              color: 'var(--color-text)',
              border: '1px solid rgba(255,255,255,0.10)',
              borderRadius: 'var(--radius-card)',
              padding: '0.5rem 0.75rem',
              fontSize: 'var(--text-sm)',
              resize: 'vertical',
              fontFamily: 'inherit',
            }}
          />
        </label>

        {/* Sync to Google */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: 'var(--text-sm)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={!!form.syncToGoogle}
            onChange={e => set('syncToGoogle', e.target.checked)}
            style={{ accentColor: 'var(--color-primary)' }}
          />
          Sincronizza con Google Calendar
        </label>
      </div>

      <style>{`
        .spin { animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </Modal>
  );
}
